import type { ShopperAiTagId } from "@redotech/effect-api-shared/ids/definitions";
import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import { useRequiredContext } from "@redotech/react-util/context";
import { useSearch } from "@redotech/react-util/search";
import { getUserAvatarUrl } from "@redotech/redo-merchant-app-common/get-avatar-url";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { GetUser as User } from "@redotech/redo-model/user";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoList } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { ObjectId } from "bson";
import Fuse from "fuse.js";
import { memo, useMemo, useRef, useState } from "react";
import { TagSelectorDropdownContent } from "../components/tag-selector-dropdown-content";

const userSearcher = new Fuse<User>([], {
  keys: ["name", "email"],
  threshold: 0.3,
});

export const BulkAssignModal = memo(function BulkAssignModal({
  onClose,
  onConfirm,
}: {
  onClose: () => void;
  onConfirm: (userId: string) => void;
}) {
  const team = useRequiredContext(TeamContext);
  const [searchText, setSearchText] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(
    undefined,
  );
  const [focusedIndex, setFocusedIndex] = useState<number | undefined>(
    undefined,
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const allUsers = useMemo(
    () => team.users.map((u) => u.user as User),
    [team.users],
  );
  const filteredUsers = useSearch(userSearcher, allUsers, searchText);

  const userListItems: RedoListItem<User>[] = useMemo(
    () =>
      filteredUsers.map((user) => ({
        type: "text" as const,
        id: MongoUserId.fromObjectId(new ObjectId(user._id)),
        text: user.name,
        value: user,
        leadingItem: {
          type: "avatar" as const,
          alt: "User profile picture",
          imageUrl: getUserAvatarUrl({ email: user.email }),
          name: user.name,
        },
      })),
    [filteredUsers],
  );

  return (
    <RedoModal
      isOpen
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Assign",
        onClick: () => {
          if (selectedUserId) {
            onConfirm(selectedUserId);
          }
        },
        disabled: !selectedUserId,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      title="Assign tickets"
    >
      <Flex dir="column" gap="md" ref={containerRef}>
        <RedoTextInput
          fullWidth
          placeholder="Search users..."
          setValue={setSearchText}
          size="sm"
          value={searchText}
        />
        {userListItems.length === 0 ? (
          <Text fontSize="sm" textColor="tertiary">
            {searchText ? "No matching users" : "No users available"}
          </Text>
        ) : (
          <RedoList
            focusedIndex={focusedIndex}
            items={userListItems}
            itemSelected={({ item }) => setSelectedUserId(item.value._id)}
            refToListenTo={containerRef.current}
            selectedItems={selectedUserId}
            setFocusedIndex={setFocusedIndex}
            size="sm"
          />
        )}
      </Flex>
    </RedoModal>
  );
});

export const BulkAddTagsModal = memo(function BulkAddTagsModal({
  onClose,
  onConfirm,
}: {
  onClose: () => void;
  onConfirm: (tagIds: string[]) => void;
}) {
  const [selectedTagIds, setSelectedTagIds] = useState<ShopperAiTagId[]>([]);

  return (
    <RedoModal
      isOpen
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Add tags",
        onClick: () => onConfirm(selectedTagIds),
        disabled: selectedTagIds.length === 0,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      title="Add tags to tickets"
    >
      <TagSelectorDropdownContent
        currentTagIds={selectedTagIds}
        onTagsChanged={setSelectedTagIds}
      />
    </RedoModal>
  );
});

export const BulkRemoveTagsModal = memo(function BulkRemoveTagsModal({
  onClose,
  onConfirm,
}: {
  onClose: () => void;
  onConfirm: (tagIds: string[]) => void;
}) {
  const [selectedTagIds, setSelectedTagIds] = useState<ShopperAiTagId[]>([]);

  return (
    <RedoModal
      isOpen
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Remove tags",
        onClick: () => onConfirm(selectedTagIds),
        disabled: selectedTagIds.length === 0,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      title="Remove tags from tickets"
    >
      <TagSelectorDropdownContent
        currentTagIds={selectedTagIds}
        onTagsChanged={setSelectedTagIds}
      />
    </RedoModal>
  );
});
